import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Box,
} from '@mui/material';
import { getUser } from 'API';
import { getBurgerMenuItems } from 'API/Home/burgerMenuItems';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { SearchTypeEnum } from 'Enum/SearchTypeEnum';
import {
  useDictionary,
  useDispatchApp,
  useImposter,
  useIsDesktop,
} from 'Hooks';
import { useIsLawyer } from 'Hooks/useIsLawyer';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  setImposterId,
  setImposterType,
  setIsAdmin,
  setFakeUser,
  setUser,
  setIsReadNotifications,
  setRepresentativeName,
} from 'Store/ImposterSlice';
import { setMenuItems } from 'Store/MenuItemsSlice';

export const UserInfo = () => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.General);
  const [input, setInput] = useState<string>('');
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const isDesktop = useIsDesktop();
  const dispatch = useDispatchApp();
  const isLawyer = useIsLawyer();
  const { authenticatedUser, imposterId, imposterType, isAdmin } =
    useImposter();

  const [selectId, setSelectId] = useState<string>('');

  const authorizedCaseSearchUsers = [
    '999410772',
    '036498079',
    '310290945',
    '999573322',
    '999736804',
    '999353402',
    '999284250',
    '313240798',
    '999488224',
    '025640087',
    '999168107',
    '200738433',
    '308118801',
    '999828189',
    '212333264',
    '207279373',
    '999406580',
  ];

  const fakeIds = [
    '037637279',
    '021716477',
    '051827178',
    '2213593',
    '51796472',
    '350488',
    '02662799',
    '73379547',
    '56075070',
    '71375331',
    '308471200',
    '327146577',
    '302536016',
    '64902737',
    '24252462',
    '23321672',
    '59310920',
    '200691988',
    '16702854',
    '207919234',
    '59246496',
    '57155129',
    '55997555',
    '51782662',
    '302536032',
  ];

  const firstRender = useRef(true);

  const isAuthorizedForCaseSearch = useCallback(() => {
    if (!authenticatedUser?.israeliId) return false;
    return authorizedCaseSearchUsers.includes(authenticatedUser.israeliId);
  }, [authenticatedUser?.israeliId]);

  const initUser = useCallback(async () => {
    const userData = await getUser();
    if (userData) {
      if (firstRender.current) {
        dispatch(setIsAdmin(userData.isAdminAuthenticatedUser));
      }
      dispatch(setUser(userData.authenticatedUser));
      if (userData.representativeName) {
        dispatch(setRepresentativeName(userData.representativeName));
      }
      dispatch(setIsReadNotifications(userData.hasUnreadedNotifications));
      if (imposterId && userData.fakeUser) {
        dispatch(setFakeUser(userData.fakeUser));
      }
    } else {
      setSelectId('');
      setInput('');
      dispatch(setImposterId(''));
      dispatch(setImposterType(SearchTypeEnum.ID));
    }
    firstRender.current = false;
  }, [dispatch, imposterId]);

  useEffect(() => {
    initUser();
  }, [imposterId, initUser]);

  const handleSearchTypeChange = (
    _: React.MouseEvent<HTMLElement>,
    newType: SearchTypeEnum | null,
  ) => {
    if (newType !== null) {
      dispatch(setImposterType(newType));
      setInput('');
      setSelectId('');
    }
  };

  const onChangeHandler = (value: string) => {
    setInput(value);
    if (imposterType === SearchTypeEnum.ID) {
      setSelectId(value);
    }
  };

  const onClickHandler = async () => {
    dispatch(setImposterId(input || ''));
    const menuItemsResult = await getBurgerMenuItems(isLawyer);
    if (menuItemsResult) {
      dispatch(setMenuItems(menuItemsResult));
    }
  };

  if (!authenticatedUser) return null;

  return (
    <>
      <Stack
        sx={{
          width: 'fit-content',
          flexDirection: isDesktop ? 'row' : 'column',
          justifyContent: 'center',
          margin: '0 auto',
          display: isHidden ? 'none' : undefined,
          zIndex: 1,
          gap: 1,
          mb: 2,
        }}
      >
        {isAdmin && isAuthorizedForCaseSearch() && (
          <Box>
            <Tooltip title={dictionary.hideImpersonationTooltip} arrow>
              <Button
                variant='outlined'
                sx={{
                  height: 32,
                  minWidth: 50,
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  padding: '0 8px',
                  background: 'white',
                }}
                onClick={() => setIsHidden(true)}
              >
                {dictionary.hideImpersonation}
              </Button>
            </Tooltip>
          </Box>
        )}

        {isAdmin && (
          <Grid
            container
            alignItems='center'
            sx={{ position: 'relative', gap: '6px' }}
          >
            {isAuthorizedForCaseSearch() && (
              <ToggleButtonGroup
                value={imposterType}
                exclusive
                onChange={handleSearchTypeChange}
                aria-label='search type'
                sx={{
                  height: 32,
                  backgroundColor: '#fff',
                  borderRadius: '6px',
                  '& .MuiToggleButton-root': {
                    height: 32,
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    padding: '0 12px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  },
                }}
              >
                <ToggleButton
                  value={SearchTypeEnum.ID}
                  aria-label='search by id'
                >
                  {dictionary.searchById}
                </ToggleButton>
                <ToggleButton
                  value={SearchTypeEnum.CaseNumber}
                  aria-label='search by case number'
                >
                  {dictionary.searchByCaseNumber}
                </ToggleButton>
              </ToggleButtonGroup>
            )}

            {imposterType === SearchTypeEnum.ID &&
              import.meta.env.DEV &&
              !isLawyer &&
              isAuthorizedForCaseSearch() && (
                <FormControl
                  variant='outlined'
                  size='small'
                  sx={{
                    minWidth: 100,
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    '& .MuiOutlinedInput-root': {
                      height: 32,
                      borderRadius: '6px',
                      '& fieldset': {
                        borderRadius: '6px',
                      },
                    },
                    '& .MuiSelect-select': {
                      padding: '8px 12px 8px 28px',
                      fontSize: '0.875rem',
                    },
                    '& .MuiSelect-icon': {
                      right: 'auto',
                      left: 8,
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.875rem',
                      top: '-2px',
                      maxWidth: 'none',
                      overflow: 'visible',
                    },
                  }}
                >
                  <InputLabel id='fake-ids-label'>
                    {dictionary.fakeIds}
                  </InputLabel>
                  <Select
                    labelId='fake-ids-label'
                    id='fake-ids'
                    value={selectId}
                    label={dictionary.fakeIds}
                    onChange={(e) => onChangeHandler(e.target.value)}
                    MenuProps={{
                      sx: {
                        maxHeight: isDesktop ? '600px' : '300px',
                        zIndex: 10000,
                      },
                    }}
                  >
                    <MenuItem value=''>
                      <em>{dictionary.none}</em>
                    </MenuItem>
                    {fakeIds.map((fakeId) => (
                      <MenuItem value={fakeId} key={fakeId}>
                        {fakeId}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

            <TextField
              size='small'
              value={input}
              onChange={(e) => onChangeHandler(e.target.value)}
              label={
                imposterType === SearchTypeEnum.ID
                  ? dictionary.idNumber
                  : dictionary.caseNumber
              }
              sx={{
                width: 100,
                '& .MuiOutlinedInput-root': {
                  height: 32,
                  borderRadius: '6px',
                  background: 'white',
                  '& fieldset': {
                    borderRadius: '6px',
                  },
                  '& input': {
                    padding: '8px 12px',
                    fontSize: '0.875rem',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem',
                  top: '-2px',
                  maxWidth: 'none',
                  overflow: 'visible',
                },
              }}
            />

            <Button
              variant='contained'
              size='small'
              sx={{
                height: 32,
                minWidth: 80,
                ml: 1,
                borderRadius: '6px',
                fontSize: '0.875rem',
                padding: '0 12px',
              }}
              onClick={onClickHandler}
            >
              {dictionary.imposter}
            </Button>
          </Grid>
        )}
      </Stack>
    </>
  );
};
